import { v4 as uuidv4 } from 'uuid';
import { addLogToReport } from '@americanexpress/one-app-ducks';
import { getClientContext } from '../utils/deviceUtils';
import {
  DASHBOARD_FAILURE,
  DASHBOARD_SUCCESS, HEADER_PWB_JWT_TOKEN, HEADER_TRACKING_ID, VRP_DASHBOARD_PATH,
} from '../constants/apiConstants';
import { DELETE_CONSENT_SUCCESS, DELETE_CONSENT_FAILURE } from './deleteConsentApi';
import { GET_VRP_DETAILS_FAILURE, GET_VRP_DETAILS_SUCCESS } from './getVrpDetailsApi';

const buildDashboardRequest = (queryParams) => {
  const headers = {
    'Content-Type': 'application/json',
    tracking_id: uuidv4(),
  };
  return {
    method: 'POST',
    credentials: 'include',
    headers,
    body: JSON.stringify({
      account_reference: '...',
      merchant_id: '...',
      country_code: 'GB',
      // Spread any query params received from the URL into the request body
      ...queryParams,
    }),
  };
};

const getConsentBucketsForLog = (body) => {
  const consentDetails = body?.vrp_consent_details ?? {};
  return {
    activeConsent: Array.isArray(consentDetails.ACTIVE) ? consentDetails.ACTIVE : [],
    cancelledConsent: Array.isArray(consentDetails.CANCELLED) ? consentDetails.CANCELLED : [],
    deletePendingConsent: Array.isArray(consentDetails.DELETE_PENDING)
      ? consentDetails.DELETE_PENDING : [],
    expiredConsent: Array.isArray(consentDetails.EXPIRED) ? consentDetails.EXPIRED : [],
  };
};

const initialState = {
  isLoading: true,
  data: null,
  error: null,
};

const parseJsonBody = async (response) => {
  const rawBody = await response.text();
  if (!rawBody) {
    return null;
  }

  try {
    return JSON.parse(rawBody);
  } catch (error) {
    throw new Error(`Unable to parse dashboard response JSON: ${error.message}`);
  }
};

const handleDashboardHttpError = (response, body, { dispatch }) => {
  dispatch(addLogToReport('info', 'Dashboard request failed', {
    moduleID: 'axp-bank-permissions-dashboard-root',
    apiPath: VRP_DASHBOARD_PATH,
    trackingId: response.headers?.get(HEADER_TRACKING_ID),
    status: response.status,
    ...getClientContext(),
  }));
  dispatch({
    type: DASHBOARD_FAILURE,
    payload: body?.status_message || `Dashboard request failed with status ${response.status}`,
    httpStatus: response.status,
  });
};

export default function dashboardReducer(state = initialState, action = {}) {
  switch (action.type) {
    case DASHBOARD_SUCCESS:
      return {
        ...state,
        isLoading: false,
        data: action.payload,
        trackingId: action.payload.trackingId || state.trackingId,
        xPwbJwtToken: action.payload.xPwbJwtToken || state.xPwbJwtToken,
        error: null,
      };
    case DELETE_CONSENT_SUCCESS:
      return {
        ...state,
        trackingId: action.payload.trackingId || state.trackingId,
        xPwbJwtToken: action.payload.xPwbJwtToken || state.xPwbJwtToken,
      };
    case GET_VRP_DETAILS_SUCCESS:
      return {
        ...state,
        data: action.payload,
        trackingId: action.payload.trackingId || state.trackingId,
        xPwbJwtToken: action.payload.xPwbJwtToken || state.xPwbJwtToken,
        error: null,
      };
    case GET_VRP_DETAILS_FAILURE:
      return {
        ...state,
        isLoading: false,
        error: action.payload,
        httpStatus: action.httpStatus ?? null,
      };
    case DELETE_CONSENT_FAILURE:
      return {
        ...state,
        httpStatus: action.httpStatus ?? null,
      };
    case DASHBOARD_FAILURE:
      return {
        isLoading: false, data: null, error: action.payload, httpStatus: action.httpStatus ?? null,
      };
    default:
      return state;
  }
}

export const fetchDashboard = ({
  apiUrl, fetchClient, queryParams = {},
}) => async (dispatch) => {
  dispatch(addLogToReport('info', 'Dashboard request details', {
    moduleID: 'axp-bank-permissions-dashboard-root',
    apiPath: VRP_DASHBOARD_PATH,
    payload: queryParams,
    ...getClientContext(),
  }));
  try {
    const request = buildDashboardRequest(queryParams);
    const response = await fetchClient(
      `${apiUrl}${VRP_DASHBOARD_PATH}`,
      request
    );
    const body = await parseJsonBody(response);

    if (!response.ok) {
      handleDashboardHttpError(response, body, { dispatch });
      return;
    }

    const trackingId = response.headers?.get(HEADER_TRACKING_ID);
    const xPwbJwtToken = response.headers?.get(HEADER_PWB_JWT_TOKEN);

    dispatch({
      type: DASHBOARD_SUCCESS,
      payload: {
        status: response.status,
        body,
        trackingId,
        xPwbJwtToken,
      },
    });
    dispatch(addLogToReport('info', 'Dashboard request success', {
      moduleID: 'axp-bank-permissions-dashboard-root',
      route: '/',
      apiPath: VRP_DASHBOARD_PATH,
      trackingId: response.headers?.get(HEADER_TRACKING_ID),
      status: response.status,
      ...getConsentBucketsForLog(body),
      ...getClientContext(),
    }));
  } catch (error) {
    dispatch({
      type: DASHBOARD_FAILURE,
      payload: error?.message || 'Unable to load dashboard',
    });
    dispatch(addLogToReport('warn', 'Dashboard request failed', {
      moduleID: 'axp-bank-permissions-dashboard-root',
      route: '/',
      message: `'Dashboard request failed in catch block' ${error}`,
      ...getClientContext(),
    }));
  }
};
